import { useState } from 'react';
import PropTypes from 'prop-types';
import { useSettings } from '../../context/SettingsContext';
import { useModal } from '../../context/ModalContext';
import { useI18n } from '../../context/I18nContext';
import { useMatrixClient } from '../../context/MatrixContext';
import SettingsToggle from './SettingsToggle';
import './FirstContactPermissionsPage.css';

const VERSICHERTE_GROUP_NAME = 'isInsuredPerson';

/**
 * Incoming Invites Page
 * 
 * Allows users to control who can send them room invites.
 * 
 * Storage Strategy:
 * - Settings stored in Matrix account data (de.gematik.tim.account.permissionconfig.pro.v1)
 * - Format follows TI-M permissionConfig schema (see api-ti-messenger/src/schema/TI-M_Pro/permissionConfig_V1.json)
 * - Automatically syncs across all user devices via Matrix
 * - Supports both Matrix user IDs (@user:server.com) and domains (server.com)
 * 
 * Schema Format:
 * {
 *   "defaultSetting": "allow all" | "block all",
 *   "serverExceptions": { "server.com": {}, ... },
 *   "userExceptions": { "@user:server.com": {}, ... },
 *   "groupExceptions": []
 * }
 * 
 * Implementation:
 * - Invites are filtered client-side when received
 * - Blocked invites are auto-rejected before appearing in UI
 * - Does NOT use m.ignored_user_list (which blocks all events, not just invites)
 * 
 * Note: Matrix doesn't have native "invite-only" blocking. This is a client-side
 * feature, but settings sync across devices via account data following TI-M schema.
 */
const FirstContactPermissionsPage = ({ onBack }) => {
  const { settings, updateSetting } = useSettings();
  const { showConfirm, showError } = useModal();
  const { t } = useI18n();
  const client = useMatrixClient();

  // Read settings directly — SettingsContext is the single source of truth.
  // A local useState mirror here would create a second feedback loop where
  // every inbound /sync event triggers a save, amplifying stale-echo races.
  const defaultPermission = settings.firstContactDefaultPermission || 'allow-all';
  const blockedList = settings.firstContactBlockedList || [];
  const allowedList = settings.firstContactAllowedList || [];

  // Local-only UI state.
  const [exceptionInput, setExceptionInput] = useState('');
  const [inputError, setInputError] = useState('');

  const currentField = defaultPermission === 'allow-all'
    ? 'firstContactBlockedList'
    : 'firstContactAllowedList';
  const currentList = defaultPermission === 'allow-all' ? blockedList : allowedList;
  const setCurrentList = (next) => updateSetting(currentField, next);
  const listTitle = defaultPermission === 'allow-all'
    ? t('FirstContactPermissions.blockedDomainsUsers', {}, 'Blocked Domains and Users')
    : t('FirstContactPermissions.allowedDomainsUsers', {}, 'Allowed Domains and Users');

  const placeholderText = t('FirstContactPermissions.inputPlaceholder', {}, 'Enter user ID or domain (e.g., @user:server.com or example.com)');

  const handleDefaultPermissionChange = (value) => {
    updateSetting('firstContactDefaultPermission', value);
  };

  // Validation patterns for Matrix user IDs and domains
  // Matrix user ID format: @localpart:domain (localpart can contain a-z, 0-9, ._=-/)
  const MATRIX_USER_ID_REGEX = /^@[a-z0-9._=\-/]+:[a-zA-Z0-9][a-zA-Z0-9.-]*\.[a-zA-Z]{2,}$/i;
  // Domain format: valid hostname with at least one dot
  const DOMAIN_REGEX = /^[a-zA-Z0-9][a-zA-Z0-9.-]*\.[a-zA-Z]{2,}$/;

  const handleAddException = () => {
    // Clear any previous error
    setInputError('');

    if (!exceptionInput.trim()) return;

    // Normalize to lowercase for case-insensitive storage and comparison
    const trimmedValue = exceptionInput.trim().toLowerCase();
    const isUser = trimmedValue.startsWith('@');

    // Validate format
    if (isUser) {
      if (!MATRIX_USER_ID_REGEX.test(trimmedValue)) {
        setInputError(t('FirstContactPermissions.invalidUserIdFormat', {}, 'Invalid user ID format. Use @user:domain.com'));
        return;
      }

      // Check if user is trying to add their own Matrix ID
      const currentUserId = client?.getUserId();
      if (currentUserId && trimmedValue === currentUserId.toLowerCase()) {
        showError(
          t('FirstContactPermissions.cannotAddSelfTitle', {}, 'Cannot Add Yourself'),
          t('FirstContactPermissions.cannotAddSelfMessage', {}, 'You cannot add your own Matrix ID to the permissions list.'),
        );
        setExceptionInput('');
        return;
      }
    } else {
      if (!DOMAIN_REGEX.test(trimmedValue)) {
        setInputError(t('FirstContactPermissions.invalidDomainFormat', {}, 'Invalid domain format. Use domain.com'));
        return;
      }
    }

    // Check if the value already exists in the list (case-insensitive via normalized storage)
    const isDuplicate = currentList.some(
      item => item.value === trimmedValue,
    );

    if (isDuplicate) {
      setInputError(t('FirstContactPermissions.duplicateEntry', {}, 'This entry already exists in the list'));
      setExceptionInput('');
      return;
    }

    const newId = currentList.length > 0 
      ? Math.max(...currentList.map(item => item.id)) + 1 
      : 1;
    
    setCurrentList([
      ...currentList,
      {
        id: newId,
        value: trimmedValue,
        type: isUser ? 'user' : 'domain',
      },
    ]);
    setExceptionInput('');
  };

  const handleRemoveException = (id) => {
    setCurrentList(currentList.filter(item => item.id !== id));
  };

  const handleClearAll = () => {
    const isBlocked = defaultPermission === 'allow-all';
    showConfirm(
      isBlocked
        ? t('FirstContactPermissions.clearAllBlockedTitle', {}, 'Clear All Blocked Entries?')
        : t('FirstContactPermissions.clearAllAllowedTitle', {}, 'Clear All Allowed Entries?'),
      isBlocked
        ? t('FirstContactPermissions.clearAllBlockedMessage', {}, 'Are you sure you want to remove all blocked domains and users? This action cannot be undone.')
        : t('FirstContactPermissions.clearAllAllowedMessage', {}, 'Are you sure you want to remove all allowed domains and users? This action cannot be undone.'),
      () => {
        // Preserve type:'group' entries — "Clear All" is scoped to the
        // MXID/domain table only, which is what the confirm copy promises
        // ("remove all blocked domains and users"). The Versicherte group
        // toggle lives in a separate section and must not be wiped.
        // Functional updater: avoids a stale snapshot if AccountData sync
        // lands a new row between click and confirm.
        setCurrentList(prev => prev.filter(item => item.type === 'group'));
      },
      null,
      t('FirstContactPermissions.clearAllButton', {}, 'Clear All'),
      t('FirstContactPermissions.cancelButton', {}, 'Cancel'),
    );
  };

  const handleExceptionInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddException();
    }
  };

  const versicherteEnabled = currentList.some(
    item => item.type === 'group' && item.value === VERSICHERTE_GROUP_NAME,
  );

  const handleVersicherteToggle = (nextChecked) => {
    if (nextChecked) {
      if (versicherteEnabled) return;
      const newId = currentList.length > 0
        ? Math.max(...currentList.map(item => item.id)) + 1
        : 1;
      setCurrentList([
        ...currentList,
        { id: newId, value: VERSICHERTE_GROUP_NAME, type: 'group' },
      ]);
    } else {
      setCurrentList(
        currentList.filter(
          item => !(item.type === 'group' && item.value === VERSICHERTE_GROUP_NAME),
        ),
      );
    }
  };

  const versicherteDescription = defaultPermission === 'allow-all'
    ? t('FirstContactPermissions.versicherteBlockDescription', {}, 'Block all invites from users whose homeserver is an insurance provider. This adds all insured users to the exception list below.')
    : t('FirstContactPermissions.versicherteAllowDescription', {}, 'Allow all invites from users whose homeserver is an insurance provider. This adds all insured users to the exception list below.');

  return (
    <div className="first-contact-permissions-page">
      <div className="settings-modal-header">
        <button className="back-button" onClick={onBack} aria-label={t('FirstContactPermissions.goBack', {}, 'Go back to settings')}>
          <ion-icon name="chevron-back-outline" />
        </button>
        <span className="settings-page-title">{t('FirstContactPermissions.pageTitle', {}, 'Incoming Invites')}</span>
      </div>

      <div className="first-contact-permissions-content">
        {/* Default Permissions Section */}
        <div className="permissions-section">
          <div className="permissions-section-title">{t('FirstContactPermissions.defaultPermissions', {}, 'Default Permissions')}</div>
          <div className="permissions-radio-group">
            <label className="radio-option">
              <input
                type="radio"
                name="default-permission"
                value="allow-all"
                checked={defaultPermission === 'allow-all'}
                onChange={() => handleDefaultPermissionChange('allow-all')}
              />
              <span className="radio-label">{t('FirstContactPermissions.allowAll', {}, 'Allow all')}</span>
            </label>
            <label className="radio-option">
              <input
                type="radio"
                name="default-permission"
                value="block-all"
                checked={defaultPermission === 'block-all'}
                onChange={() => handleDefaultPermissionChange('block-all')}
              />
              <span className="radio-label">{t('FirstContactPermissions.blockAll', {}, 'Block all')}</span>
            </label>
          </div>
        </div>

        {/* Group Exceptions Section */}
        <div className="permissions-section">
          <div className="permissions-section-title">
            {t('FirstContactPermissions.groupExceptionsTitle', {}, 'Group Exceptions')}
          </div>
          <div
            className="group-exception-row"
            data-testid="group-versicherte-toggle"
          >
            <SettingsToggle
              label={t('FirstContactPermissions.versicherteLabel', {}, 'Versicherte (all insured persons)')}
              description={versicherteDescription}
              checked={versicherteEnabled}
              onChange={handleVersicherteToggle}
            />
          </div>
        </div>

        {/* Exceptions Section */}
        <div className="permissions-section">
          <div className="permissions-section-title">{t('FirstContactPermissions.exceptions', {}, 'Exceptions')}</div>
          <div className="exception-input-group">
            <input
              type="text"
              className={`exception-input${inputError ? ' input-error' : ''}`}
              placeholder={placeholderText}
              value={exceptionInput}
              onChange={(e) => {
                setExceptionInput(e.target.value);
                if (inputError) setInputError('');
              }}
              onKeyDown={handleExceptionInputKeyDown}
              aria-invalid={!!inputError}
              aria-describedby={inputError ? 'exception-input-error' : undefined}
            />
            <button
              className="add-exception-button"
              onClick={handleAddException}
              disabled={!exceptionInput.trim()}
            >
              {t('FirstContactPermissions.addException', {}, 'Add Exception')}
            </button>
          </div>
          {inputError && (
            <div id="exception-input-error" className="exception-error-message" role="alert">
              {inputError}
            </div>
          )}
        </div>

        {/* Blocked/Allowed Users Table (users and domains only; groups shown above) */}
        <div className="permissions-section">
          {(() => {
            const mxidDomainRows = currentList.filter(i => i.type !== 'group');
            if (mxidDomainRows.length === 0) {
              return (
                <div className="empty-blocked-list">
                  <div className="empty-blocked-list-title">{listTitle}</div>
                  <div className="empty-blocked-list-message">
                    {defaultPermission === 'allow-all'
                      ? t('FirstContactPermissions.noBlockedItems', {}, 'No blocked domains or users')
                      : t('FirstContactPermissions.noAllowedItems', {}, 'No allowed domains or users')}
                  </div>
                </div>
              );
            }
            return (
              <div className="blocked-list-table">
                <table>
                  <thead>
                    <tr>
                      <th colSpan="2" className="table-header-merged">
                        <div className="table-header-content">
                          <span>{listTitle}</span>
                          <button
                            className="clear-all-button"
                            onClick={handleClearAll}
                            aria-label={t('FirstContactPermissions.clearAllAriaLabel', { listTitle }, `Clear all ${listTitle.toLowerCase()}`)}
                            title={t('FirstContactPermissions.clearAllTooltip', {}, 'Clears all entries')}
                          >
                            <ion-icon name="trash-outline" />
                          </button>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {mxidDomainRows.map((item) => (
                      <tr key={item.id}>
                        <td className="value-cell">{item.value}</td>
                        <td>
                          <button
                            className="remove-button"
                            onClick={() => handleRemoveException(item.id)}
                            aria-label={t('FirstContactPermissions.removeAriaLabel', { value: item.value }, `Remove ${item.value}`)}
                          >
                            <ion-icon name="trash-outline" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

FirstContactPermissionsPage.propTypes = {
  onBack: PropTypes.func.isRequired,
};

export default FirstContactPermissionsPage;
